import type { User } from "@supabase/supabase-js";

import { supabase } from "./supabaseClient";

/**
 * Get the current user from the database
 * @returns The current user
 */
export const getCurrentUser = async (): Promise<User> => {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  if (!data.user) throw new Error("Failed to retrieve current user");
  return data.user;
};

/**
 * Read a value from the user's metadata object.
 * @param user - The user object
 * @param key - The metadata key to retrieve
 * @returns The metadata value, or undefined if not found
 */
export const getMetadataValue = <T = unknown>(user: User, key: string): T | undefined => {
  return user.user_metadata?.[key] as T | undefined;
};

/**
 * Write a single key to the current user's metadata.
 * Supabase performs a shallow merge — other existing keys are preserved.
 * @param key - The metadata key to set
 * @param value - The value to set
 * @returns The updated user object
 */
export const setMetadataValueForAuthenticatedUser = async <T = unknown>(
  key: string,
  value: T
): Promise<User> => {
  const { error } = await supabase.auth.updateUser({
    data: { [key]: value },
  });
  if (error) throw error;
  return getCurrentUser();
};

/**
 * Fetch a template name by ID.
 * @param templateId - The template UUID
 * @returns The template name, or null if not found
 */
export const getTemplateName = async (templateId: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from("templates")
      .select("name")
      .eq("id", templateId)
      .single();

    if (error) throw error;
    return data?.name ?? null;
  } catch (error) {
    console.error("Error fetching template name:", error);
    return null;
  }
};
